import React from 'react'
import FacebookLogo from './flogo.svg'
import styles from './styles.css'
import config from '#src/config'
import Login, { ReactFacebookFailureResponse, ReactFacebookLoginInfo } from 'react-facebook-login'
import classNames from 'classnames'
import { isConsumerMobileNative } from '#src/services/mobile'
import { FacebookSigninResponse } from '#src/services/dealNativeBridge'
import {
  WebMessageEventEmitterConsumer,
  WebMessageEventEmitter
} from '#src/containers/ReceiveMessageFromNative'
import uuidv4 from 'uuid/v4'

interface FacebookLoginProps {
  className?: string
  scope?: string
  fields?: string
  text: string
  onSuccess: (response: FacebookLoginResponse) => void
  onFailure?: (error: ReactFacebookFailureResponse) => void
}

export interface FacebookLoginResponse extends ReactFacebookLoginInfo {
  first_name?: string
  last_name?: string
}

export default class FacebookLogin extends React.Component<FacebookLoginProps> {
  public render() {
    return (
      <WrappedLoginWebOrNative
        {...this.props}
        onSuccess={this.props.onSuccess}
        onFailure={this.props.onFailure || (() => void null)}
      >
        <div className={styles.container}>
          <div className={styles.logo}>
            <FacebookLogo className={styles.icon} />
          </div>
          <div className={styles.text}>{this.props.text}</div>
        </div>
      </WrappedLoginWebOrNative>
    )
  }
}

interface WrappedLoginWebOrNativeProps {
  className?: string
  scope?: string
  fields?: string
  onSuccess: (response: ReactFacebookLoginInfo) => void
  onFailure: (error: any) => void
  children: React.ReactNode
}

const WrappedLoginWebOrNative: React.SFC<WrappedLoginWebOrNativeProps> = props => (
  <WebMessageEventEmitterConsumer>
    {eventEmitter => <LoginWebOrNative {...props} eventEmitter={eventEmitter} />}
  </WebMessageEventEmitterConsumer>
)

interface LoginWebOrNativeProps extends WrappedLoginWebOrNativeProps {
  eventEmitter?: WebMessageEventEmitter
}

class LoginWebOrNative extends React.Component<LoginWebOrNativeProps> {
  constructor(props: LoginWebOrNativeProps) {
    super(props)

    this.handleNativeClick = this.handleNativeClick.bind(this)
  }

  private handleNativeClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    const { eventEmitter, scope, fields, onSuccess, onFailure } = this.props
    eventEmitter!
      .requestResponse<FacebookSigninResponse>(
        {
          type: 'facebookSigninRequest',
          messageId: uuidv4(),
          permissions: (scope && scope.split(',')) || [],
          fields: fields || ''
        },
        'facebookSigninResponse'
      )
      .then(message => {
        if (message.success) {
          onSuccess(message.success)
        } else {
          onFailure({
            status: message.failureCode
          })
        }
      })
  }

  render() {
    // Don't SSR since we can't determine the difference between being embedded in
    // the consumer mobile native app vs regular web at this point. If we allow this
    // to SSR, the server will render the <Login> component, and then the mobile native web
    // app (web view wrapper) will rehydrate DOM generated by <Login> with the virtual dom
    // in the regular <button> code path, leading to some visual awkwardness.
    if (process.env.TARGET === 'node') {
      return null
    }

    const { className, children, eventEmitter, scope, fields } = this.props
    if (isConsumerMobileNative() && eventEmitter) {
      return (
        <button className={classNames(styles.button, className)} onClick={this.handleNativeClick}>
          {children}
        </button>
      )
    } else {
      return (
        <Login
          appId={config.get('facebook.app_id')}
          icon={children}
          cssClass={classNames(styles.button, className)}
          scope={scope}
          fields={fields}
          disableMobileRedirect={true}
          textButton=""
          callback={this.props.onSuccess}
          onFailure={this.props.onFailure}
        />
      )
    }
  }
}
